export const DIRECTIONS = ["N", "E", "S", "W"] as const;
export type Direction = (typeof DIRECTIONS)[number];
export type Rotation = 0 | 90 | 180 | 270;
export type EdgeType = "empty" | "tube" | "colony";
export type FeatureType = "tube" | "colony" | "solar";

export interface TileRegion {
  id: string;
  type: FeatureType;
  edges?: readonly Direction[];
  closedEndpoint?: boolean;
  launchpad?: boolean;
}

export interface TileDefinition {
  id: TileId;
  count: number;
  spriteIndex: number;
  edges: Readonly<Record<Direction, EdgeType>>;
  regions: readonly TileRegion[];
}

export const TILE_IDS = [
  "tube_straight", "tube_curve", "tube_t", "tube_cross", "tube_terminal",
  "colony_single", "colony_corner", "colony_opposite", "colony_three", "colony_full",
  "colony_tube_straight", "colony_tube_curve", "colony_tube_large", "solar", "solar_tube",
  "launchpad_colony",
] as const;
export type TileId = (typeof TILE_IDS)[number];

const tile = (
  id: TileId,
  count: number,
  spriteIndex: number,
  edges: Record<Direction, EdgeType>,
  regions: TileRegion[],
): TileDefinition => ({ id, count, spriteIndex, edges, regions });

export const TILE_CATALOG: Readonly<Record<TileId, TileDefinition>> = {
  tube_straight: tile("tube_straight", 7, 0, { N: "tube", E: "empty", S: "tube", W: "empty" }, [{ id: "tube_a", type: "tube", edges: ["N", "S"] }]),
  tube_curve: tile("tube_curve", 7, 1, { N: "tube", E: "tube", S: "empty", W: "empty" }, [{ id: "tube_a", type: "tube", edges: ["N", "E"] }]),
  tube_t: tile("tube_t", 4, 2, { N: "tube", E: "tube", S: "empty", W: "tube" }, [{ id: "tube_a", type: "tube", edges: ["N", "E", "W"] }]),
  tube_cross: tile("tube_cross", 2, 3, { N: "tube", E: "tube", S: "tube", W: "tube" }, [{ id: "tube_a", type: "tube", edges: ["N", "E", "S", "W"] }]),
  tube_terminal: tile("tube_terminal", 2, 4, { N: "tube", E: "empty", S: "empty", W: "empty" }, [{ id: "tube_a", type: "tube", edges: ["N"], closedEndpoint: true }]),
  colony_single: tile("colony_single", 6, 5, { N: "colony", E: "empty", S: "empty", W: "empty" }, [{ id: "colony_a", type: "colony", edges: ["N"] }]),
  colony_corner: tile("colony_corner", 4, 6, { N: "colony", E: "colony", S: "empty", W: "empty" }, [{ id: "colony_a", type: "colony", edges: ["N", "E"] }]),
  colony_opposite: tile("colony_opposite", 3, 7, { N: "colony", E: "empty", S: "colony", W: "empty" }, [{ id: "colony_a", type: "colony", edges: ["N", "S"] }]),
  colony_three: tile("colony_three", 3, 8, { N: "colony", E: "colony", S: "empty", W: "colony" }, [{ id: "colony_a", type: "colony", edges: ["N", "E", "W"] }]),
  colony_full: tile("colony_full", 2, 9, { N: "colony", E: "colony", S: "colony", W: "colony" }, [{ id: "colony_a", type: "colony", edges: ["N", "E", "S", "W"] }]),
  colony_tube_straight: tile("colony_tube_straight", 4, 10, { N: "tube", E: "colony", S: "tube", W: "empty" }, [
    { id: "tube_a", type: "tube", edges: ["N", "S"] },
    { id: "colony_a", type: "colony", edges: ["E"] },
  ]),
  colony_tube_curve: tile("colony_tube_curve", 3, 11, { N: "tube", E: "tube", S: "colony", W: "empty" }, [
    { id: "tube_a", type: "tube", edges: ["N", "E"] },
    { id: "colony_a", type: "colony", edges: ["S"] },
  ]),
  colony_tube_large: tile("colony_tube_large", 3, 12, { N: "tube", E: "colony", S: "colony", W: "tube" }, [
    { id: "tube_a", type: "tube", edges: ["N", "W"] },
    { id: "colony_a", type: "colony", edges: ["E", "S"] },
  ]),
  solar: tile("solar", 6, 13, { N: "empty", E: "empty", S: "empty", W: "empty" }, [{ id: "solar_a", type: "solar" }]),
  solar_tube: tile("solar_tube", 2, 14, { N: "tube", E: "empty", S: "tube", W: "empty" }, [
    { id: "tube_a", type: "tube", edges: ["N", "S"] },
    { id: "solar_a", type: "solar" },
  ]),
  launchpad_colony: tile("launchpad_colony", 2, 15, { N: "colony", E: "colony", S: "colony", W: "colony" }, [{ id: "colony_a", type: "colony", edges: ["N", "E", "S", "W"], launchpad: true }]),
};

export const START_TILE_ID: TileId = "colony_tube_straight";

export function fullTileDeck(): TileId[] {
  return TILE_IDS.flatMap((id) => Array.from({ length: TILE_CATALOG[id].count }, () => id));
}

export function playableTileDeck(): TileId[] {
  const deck = fullTileDeck();
  const start = deck.indexOf(START_TILE_ID);
  deck.splice(start, 1);
  return deck;
}

export function rotateDirection(direction: Direction, rotation: Rotation): Direction {
  return DIRECTIONS[(DIRECTIONS.indexOf(direction) + rotation / 90) % 4]!;
}

export function rotatedEdges(tileId: TileId, rotation: Rotation): Record<Direction, EdgeType> {
  const result = {} as Record<Direction, EdgeType>;
  for (const direction of DIRECTIONS) result[rotateDirection(direction, rotation)] = TILE_CATALOG[tileId].edges[direction];
  return result;
}

export function rotatedRegionEdges(tileId: TileId, regionId: string, rotation: Rotation): Direction[] {
  const region = TILE_CATALOG[tileId].regions.find((candidate) => candidate.id === regionId);
  if (!region) throw new Error("unknown_region");
  return (region.edges ?? []).map((direction) => rotateDirection(direction, rotation));
}
